/**
 * Test-only entry point for the browser-driven appearance tests over the app's
 * own screens (see `e2e-web/app-appearance.spec.ts`).
 *
 * Registered only in a dev build (see `IS_DEV`), and deliberately *not* behind
 * `requireAuth` — same reason as `FlashBookFixtures`: the Dashboard and the
 * Wallet are ordinary signed-in screens, and a fresh browser has no session to
 * sign in with. Everything rendered here is the shipping component; only the
 * lack of a session is staged.
 *
 *   /app-fixtures?screen=dashboard
 *     the real Dashboard screen, as a student sees it
 *   /app-fixtures?screen=teacher
 *     the real teacher Dashboard, which is the one that paints a card per
 *     category — `/dashboard` picks between the two from the account type, which
 *     a session-less browser cannot supply, so the choice is made here instead
 *   /app-fixtures?screen=wallet
 *     the real Wallet screen
 *   /app-fixtures?screen=capsule-post
 *     the real feed row for a capsule post, so a browser can measure the card the
 *     post is drawn by (see `e2e-web/capsule-feed-post.spec.ts`)
 *
 * Why a fixture at all, rather than visiting the real routes: a screen behind
 * `requireAuth` redirects before it paints, so a sweep over `/dashboard` would
 * measure a login page and report the Dashboard as having no surfaces. The
 * fixture is what puts the *real* screen in front of the sweep.
 */
import {useMemo} from 'react'
import {
  type AppBskyFeedDefs,
  type AppBskyFeedPost,
  moderatePost,
  type ModerationOpts,
} from '@atproto/api'
import {useRoute} from '@react-navigation/native'

import {moderationOptsOverrideContext} from '#/state/preferences/moderation-opts'
import {DashboardScreen} from '#/screens/Dashboard'
import StudentDashboard from '#/screens/Dashboard/components/StudentDashboard'
import TeacherDashboard from '#/screens/Dashboard/components/TeacherDashboard'
import {AppProvider} from '#/screens/Dashboard/contexts/AppContext'
import {BillsProvider} from '#/screens/Dashboard/contexts/BillsContext'
import {StreakProvider} from '#/screens/Dashboard/contexts/StreakContext'
import {WalletScreen} from '#/screens/Wallet'
import * as Layout from '#/components/Layout'
import {PostFeedItem} from '#/view/com/posts/PostFeedItem'

type FixtureScreen =
  | 'dashboard'
  | 'student'
  | 'teacher'
  | 'wallet'
  | 'capsule-post'

/**
 * Route params are the normal path, but a query string is what the tests can set
 * from a URL without wiring a deep link, so read both — the same contract
 * `FlashBookFixtures` uses.
 */
function useScreenParam(): FixtureScreen {
  const route = useRoute()
  const params = (route.params ?? {}) as {screen?: string}
  return useMemo(() => {
    const fromQuery =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('screen')
        : null
    const asked = (params.screen ?? fromQuery) as FixtureScreen | null
    return asked === 'teacher' ||
      asked === 'wallet' ||
      asked === 'student' ||
      asked === 'capsule-post'
      ? asked
      : 'dashboard'
  }, [params.screen])
}

/**
 * The teacher Dashboard, in the exact provider tree `DashboardScreen` builds.
 *
 * `DashboardContent` chooses between the two dashboards from the *account type*
 * on the profile, which needs a session; the fixture makes that one choice
 * explicitly instead of staging a fake account. The providers and the layout are
 * copied from the screen rather than approximated, because the sweep is
 * measuring where these surfaces sit and what they paint, and a different tree
 * would be measuring a different screen.
 */
function TeacherDashboardScreen() {
  return (
    <AppProvider>
      <BillsProvider>
        <StreakProvider role="teacher">
          <Layout.Screen>
            <Layout.Center style={{flex: 1}}>
              <TeacherDashboard />
            </Layout.Center>
          </Layout.Screen>
        </StreakProvider>
      </BillsProvider>
    </AppProvider>
  )
}

/** The student Dashboard rendered directly, so both roles are reachable. */
function StudentDashboardScreen() {
  return (
    <AppProvider>
      <BillsProvider>
        <StreakProvider role="student">
          <Layout.Screen>
            <Layout.Center style={{flex: 1}}>
              <StudentDashboard />
            </Layout.Center>
          </Layout.Screen>
        </StreakProvider>
      </BillsProvider>
    </AppProvider>
  )
}

/**
 * A capsule post, in the real feed row.
 *
 * The browser-driven layout tests need a capsule post to measure, and a film is not
 * something a page of fixtures can hand them: the card plays HLS, which arrives from a
 * CDN, and the bytes are not what is being asserted. So the film's playlist is a URL
 * the dev server never answers — which is also the honest first frame of every capsule
 * post: a film still on its way — and the post carries the still the spec routes in,
 * so there is a picture to blur either way. What a browser can measure here is the
 * card's *shape*: its height at every window size, which way the film is fitted, and
 * whether the blurred fill is behind it at all.
 *
 * That the fill really moves — the canvas painted from the playing film — is a claim
 * about pixels rather than about layout, and it is observed in the app rather than in
 * a spec; see the capsule post's own note in `.freebuff/run.md`.
 *
 *   /app-fixtures?screen=capsule-post
 */
const CAPSULE_FIXTURE_POST_URI =
  'at://did:plc:abcdefghijklmnopqrstuvwx/app.bsky.feed.post/capsule1'
/** A path the dev server does not serve, routed in by the spec that measures this. */
export const CAPSULE_FIXTURE_THUMB = '/fixture-capsule-thumb.png'

const CAPSULE_FIXTURE_CAPSULE = {
  uri: 'at://did:plc:abcdefghijklmnopqrstuvwx/app.bsky.feed.capsule/capsule1',
  /** Six exercises: what the coin row counts. */
  slideCount: 6,
  playCount: 5,
  recentPlays: [
    {
      player: {did: 'did:plc:aminabcdefghijklmnop', handle: 'amina.test'},
      score: 4,
      timeMs: 42_000,
      createdAt: '2026-09-02T09:00:00.000Z',
    },
    {
      player: {
        did: 'did:plc:youssefabcdefghijklm',
        handle: 'youssef.test',
        displayName: 'Youssef',
      },
      score: 6,
      timeMs: 51_000,
      createdAt: '2026-09-02T09:30:00.000Z',
    },
  ],
}

const CAPSULE_FIXTURE_POST = {
  $type: 'app.bsky.feed.defs#postView' as const,
  uri: CAPSULE_FIXTURE_POST_URI,
  cid: 'bafyfixturecapsule',
  author: {
    did: 'did:plc:abcdefghijklmnopqrstuvwx',
    handle: 'fixture-creator.test',
    displayName: 'Fixture Creator',
  },
  record: {
    $type: 'app.bsky.feed.post' as const,
    text: 'Six questions on fractions, for the fifth year.',
    createdAt: '2026-09-01T10:00:00.000Z',
    langs: ['en'],
    /*
     * The capsule's own marker, and the slide count on the fast path: a post carries
     * these so that a feed row can draw the coin count before any request has answered
     * (see `CapsuleFeedEmbed`).
     */
    type: 'capsule',
    slideCount: 6,
    capsule: {
      uri: 'at://did:plc:abcdefghijklmnopqrstuvwx/app.bsky.feed.capsule/capsule1',
    },
  },
  embed: {
    $type: 'app.bsky.embed.video#view' as const,
    cid: 'bafyfixturecapsulefilm',
    playlist: 'https://capsule-fixture.invalid/film.m3u8',
    thumbnail: CAPSULE_FIXTURE_THUMB,
    /** A phone's film, which is the shape the card is designed around. */
    aspectRatio: {width: 1080, height: 1920},
  },
  labels: [],
  indexedAt: '2026-09-01T10:00:00.000Z',
  likeCount: 2,
  replyCount: 0,
  repostCount: 0,
  quoteCount: 0,
  bookmarkCount: 0,
} as unknown as AppBskyFeedDefs.PostView

/**
 * The moderation context the fixture row renders against.
 *
 * The feed's own provider reads labeler preferences over the network, which a
 * signed-out browser cannot answer — and a row that never renders is not a row that can
 * be measured. The same override the FlashBook fixture uses: the real `PostFeedItem`
 * against a viewer that has no preferences, which is true of this browser.
 */
const FIXTURE_MODERATION_OPTS: ModerationOpts = {
  userDid: undefined,
  prefs: {
    adultContentEnabled: false,
    labels: {},
    labelers: [],
    mutedWords: [],
    hiddenPosts: [],
  },
  labelDefs: {},
}

function CapsulePostFixtureScreen() {
  return (
    <moderationOptsOverrideContext.Provider value={FIXTURE_MODERATION_OPTS}>
      <Layout.Screen>
        <Layout.Center style={{flex: 1}}>
          <PostFeedItem
            post={CAPSULE_FIXTURE_POST}
            record={CAPSULE_FIXTURE_POST.record as AppBskyFeedPost.Record}
            reason={undefined}
            feedContext={undefined}
            reqId={undefined}
            moderation={moderatePost(
              CAPSULE_FIXTURE_POST,
              FIXTURE_MODERATION_OPTS,
            )}
            parentAuthor={undefined}
            showReplyTo={false}
            rootPost={CAPSULE_FIXTURE_POST}
            capsule={CAPSULE_FIXTURE_CAPSULE}
            hideTopBorder
          />
        </Layout.Center>
      </Layout.Screen>
    </moderationOptsOverrideContext.Provider>
  )
}

export function AppFixturesScreen() {
  const screen = useScreenParam()

  if (screen === 'capsule-post') return <CapsulePostFixtureScreen />
  if (screen === 'wallet') return <WalletScreen />
  if (screen === 'teacher') return <TeacherDashboardScreen />
  if (screen === 'student') return <StudentDashboardScreen />
  // `dashboard` is the shipping screen, reached the shipping way.
  return <DashboardScreen />
}
